import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/common/Button';
import FormField from '../shared/FormField';
import { judgesApi, extractOne } from '../../api/facades';

const JUDGES_BASE = '/dashboard/settings/judges';

const inputClass =
  'w-full rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm text-slate-700 dark:text-slate-200 focus:border-primary-300 focus:outline-none';

const JudgeEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const numId = id ? parseInt(id, 10) : NaN;
  const [judge_name, setJudgeName] = useState('');
  const [is_active, setIsActive] = useState<'Yes' | 'No'>('Yes');

  const { data: res, isLoading, isError } = useQuery({
    queryKey: ['judges', numId],
    queryFn: () => judgesApi.get(numId),
    enabled: !Number.isNaN(numId),
  });
  const row = extractOne(res);

  useEffect(() => {
    if (!row || typeof row !== 'object') return;
    const r = row as any;
    if (r.judge_name != null) setJudgeName(String(r.judge_name));
    if (r.is_active != null) setIsActive(r.is_active === 'No' ? 'No' : 'Yes');
  }, [row]);

  const update = useMutation({
    mutationFn: () => judgesApi.update(numId, { judge_name: judge_name.trim() || undefined, is_active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['judges'] });
      toast.success('Judge updated.');
      navigate(`${JUDGES_BASE}/${numId}`);
    },
    onError: (e: any) => toast.error(e?.message || 'Update failed.'),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!judge_name.trim()) return;
    update.mutate();
  };

  if (Number.isNaN(numId)) {
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
        <p className="text-slate-600 dark:text-slate-400">Invalid ID.</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate(JUDGES_BASE)}>Back</Button>
      </div>
    );
  }

  if (isError || (!isLoading && !row)) {
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
        <h1 className="text-2xl font-bold text-slate-700 dark:text-slate-200 mb-2">Judge not found</h1>
        <Button variant="secondary" onClick={() => navigate(JUDGES_BASE)}>Back to Judges</Button>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Update judge details.</p>

      {isLoading ? (
        <p className="text-slate-500">Loading…</p>
      ) : (
        <form onSubmit={onSubmit} className="space-y-6">
          <section>
            <h2 className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-3">Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <FormField label="Name" hint="Required">
                  <input className={inputClass} value={judge_name} onChange={(e) => setJudgeName(e.target.value)} placeholder="e.g. Hon. Jane Doe" required />
                </FormField>
              </div>
              <FormField label="Active">
                <select className={inputClass} value={is_active} onChange={(e) => setIsActive(e.target.value as 'Yes' | 'No')}>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </FormField>
            </div>
          </section>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="secondary" type="button" onClick={() => navigate(JUDGES_BASE)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={!judge_name.trim() || update.isPending}>{update.isPending ? 'Saving…' : 'Save'}</Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default JudgeEditPage;
